import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Stack, Chip, Button, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Select, MenuItem,
  FormControl, InputLabel, Divider, Alert, Tooltip, IconButton,
  Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, LinearProgress, Autocomplete,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SummarizeIcon from '@mui/icons-material/Summarize';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import type { PurchaseOrder } from '../data/types';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';
import { Sparkle } from '../components/Sparkle';
import { CollapseDivider, COLLAPSE_THUMB_WIDTH } from '../components/CollapseDivider';
import { DocumentPreviewRail } from '../components/DocumentPreviewRail';
import { consumePendingStatementUpload } from '../utils/pendingStatementUpload';

const FI_ORANGE = '#F57C00';
const FI_BLUE   = '#027BFF';
const fmt = (n: number) => `$${n.toFixed(2)}`;

const PROCESSING_STEPS = [
  'Uploading statement to Alfred…',
  'Reading document structure…',
  'Extracting PO references…',
  'Matching amounts and references…',
  'Complete.',
];

type MatchStatus = 'matched' | 'amount-differs' | 'zero-po' | 'no-po';

interface StatementLine {
  id: number;
  supplierPoRef: string;   // Customer's PO ref as recorded by supplier — used to match FieldInsight PO
  supplierRef:   string;   // Supplier's own internal job/docket/invoice ref
  project?:      string;   // Project reference extracted from statement
  jobRef?:       string;   // Job number extracted from statement
  description:   string;
  amountIncGst:  number;
}

type MatchedBy = 'po-id' | 'project' | 'job-ref';

interface MatchResult {
  statLine: StatementLine;
  status: MatchStatus;
  matchedPo?: PurchaseOrder;
  matchedBy?: MatchedBy;
  variance: number;
  variancePct: number;
  checked: boolean;
  newJobRef: string;
  newPostcode: string;
}

// ── Sanity-check findings (v1.14.8) ─────────────────────────────────────────
// PRIMARY validation: does the Bill on file line up with the statement line?
// PO existence is secondary context only — a Bill can validly exist without a
// PO (free-form supplier invoice). Statements don't carry line-item detail,
// so the only meaningful audit at the statement level is:
//   Statement Invoice  <->  Our Bill  (does it exist? does the amount match?)
type FindingSeverity =
  | 'ok'                 // Exactly 1 matching Bill + amount within tolerance
  | 'amount-mismatch'    // 1 Bill found, $ differs from statement
  | 'duplicate-bill'     // 2+ Bills match the supplier invoice number
  | 'missing-bill';      // No Bill in our system for this statement line

const SEVERITY_CFG: Record<FindingSeverity, {
  label: string; short: string; color: string; bg: string; border: string;
}> = {
  ok:                { label: 'Matched',         short: 'OK',     color: '#1b5e20', bg: '#e8f5e9', border: '#a5d6a7' },
  'amount-mismatch': { label: 'Amount mismatch', short: '$ diff', color: '#ef6c00', bg: '#fff8e1', border: '#ffe082' },
  'duplicate-bill':  { label: 'Duplicate bill',  short: 'Dup',    color: '#b71c1c', bg: '#ffebee', border: '#ef9a9a' },
  'missing-bill':    { label: 'Missing bill',    short: 'No bill',color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
};

const STATUS_CONFIG: Record<MatchStatus, { label: string; color: string; bg: string }> = {
  'matched':       { label: 'Matched',      color: '#2e7d32', bg: '#e8f5e9' },
  'amount-differs':{ label: 'Needs Review', color: '#e65100', bg: '#fff3e0' },
  'zero-po':       { label: 'Set from $0',  color: '#0277bd', bg: '#e3f2fd' },
  'no-po':         { label: 'No PO Found',  color: '#6a1b9a', bg: '#f3e5f5' },
};

const PRICE_MATCH_PCT = 2; // 2% tolerance

export const StatementImportPage: React.FC = () => {
  const navigate   = useNavigate();
  const { id: routeStatementId } = useParams<{ id: string }>();
  const { purchaseOrders, suppliers, updatePurchaseOrder, addPOLineDetails, bills,
          statements, updateStatement, setStatementStatus } = useData();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The Statement entity we're working on (may be undefined for legacy /statement-import route)
  const activeStatement = routeStatementId
    ? statements.find(s => s.id === routeStatementId)
    : undefined;

  // ── Refs so callbacks always read fresh data (avoid stale closures) ──────────
  const poRef  = useRef(purchaseOrders);
  const supRef = useRef(suppliers);
  useEffect(() => { poRef.current  = purchaseOrders; }, [purchaseOrders]);
  useEffect(() => { supRef.current = suppliers;      }, [suppliers]);

  // Track whether we've hydrated from the active Statement entity (so we don't keep clobbering edits)
  const hydratedRef = useRef<string | null>(null);

  // ── File / parse state ───────────────────────────────────────────────────────
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pdfUrl,       setPdfUrl]       = useState<string | null>(null);
  const [isDragging,   setIsDragging]   = useState(false);
  const [processing,   setProcessing]   = useState(false);
  const [processStep,  setProcessStep]  = useState(0);
  const [processed,    setProcessed]    = useState(false);
  const [demoMode,     setDemoMode]     = useState(false);
  const [subTab,       setSubTab]       = useState<'pdf' | 'summary'>('pdf');

  // ── Statement header ─────────────────────────────────────────────────────────
  const [supplierId,    setSupplierId]    = useState('');
  const [statementDate, setStatementDate] = useState(new Date().toISOString().split('T')[0]);
  const [statementRef,  setStatementRef]  = useState('');

  // ── Results ──────────────────────────────────────────────────────────────────
  const [results,        setResults]        = useState<MatchResult[]>([]);
  const [appliedCount,   setAppliedCount]   = useState(0);
  const [showApplied,    setShowApplied]    = useState(false);
  const [createPoDialog,  setCreatePoDialog]  = useState<MatchResult | null>(null);
  const [addLineDialog,   setAddLineDialog]   = useState<MatchResult | null>(null);
  const [addLineTargetPo, setAddLineTargetPo] = useState('');
  const [addLineCode,     setAddLineCode]     = useState('');
  const [parseError,      setParseError]      = useState<string | null>(null);
  const [aiFailure,       setAiFailure]       = useState<null | {
    code: 'INSUFFICIENT_CREDITS' | 'RATE_LIMITED' | 'OVERLOADED' | 'BAD_API_KEY' | 'UNKNOWN';
    message: string;
    docsUrl?: string;
  }>(null);
  // Manual PO assignments for 'no-po' rows — lineId → poId
  const [manualLinks,    setManualLinks]    = useState<Record<number, string>>({});

  // ── Sanity-check findings (v1.14.8) ──────────────────────────────────────
  // PRIMARY classification: Bill vs Statement. PO match is recorded as
  // SECONDARY context only (used for follow-up actions and the side note).
  // Tolerance for amount match is 1¢ (rounding); anything bigger = mismatch.
  // The supplier's preferred way to identify an invoice is `supplierRef`
  // (= statement line's invoice no). We match Bills against that field.
  const findings = useMemo(() => {
    return results.map(r => {
      const statAmt = r.statLine.amountIncGst;
      const invRef  = r.statLine.supplierRef.trim().toLowerCase();

      // Match bills by supplierInvoiceNo (case-insensitive). Guard against
      // cross-supplier collisions: when we already have a PO context, require
      // the bill to either link to that PO or share the supplier. When we
      // don't have a PO we still want to find the bill, so fall back to a
      // supplier-id match against the active statement (if present).
      const matchedBills = invRef
        ? bills.filter(b => {
            if (b.supplierInvoiceNo.trim().toLowerCase() !== invRef) return false;
            if (r.matchedPo) {
              if (b.linkedPoId && b.linkedPoId === r.matchedPo.id) return true;
              if (b.supplierId === r.matchedPo.supplierId) return true;
              return false;
            }
            // No PO — fall back to statement-level supplier if known.
            if (supplierId && b.supplierId !== supplierId) return false;
            return true;
          })
        : [];

      // PRIMARY: classify on bill-vs-statement match.
      let severity: FindingSeverity;
      let note = '';

      if (matchedBills.length === 0) {
        severity = 'missing-bill';
        note = `No Bill on file for invoice ${r.statLine.supplierRef || '—'}.`;
      } else if (matchedBills.length > 1) {
        severity = 'duplicate-bill';
        note = `${matchedBills.length} Bills match invoice ${r.statLine.supplierRef} — possible double-payment.`;
      } else {
        const bill = matchedBills[0];
        const delta = Math.abs(statAmt - bill.totalIncGst);
        if (delta > 0.01) {
          severity = 'amount-mismatch';
          note = `Bill ${bill.billNo} is ${fmt(bill.totalIncGst)} but statement shows ${fmt(statAmt)} (Δ ${(statAmt - bill.totalIncGst >= 0 ? '+' : '')}${fmt(statAmt - bill.totalIncGst)}).`;
        } else {
          severity = 'ok';
          note = `Bill ${bill.billNo} matches at ${fmt(bill.totalIncGst)}.`;
        }
      }

      // SECONDARY: append PO context to the note for the user's benefit.
      const poNote = r.matchedPo
        ? `PO ${r.matchedPo.id} found.`
        : (r.statLine.supplierPoRef
            ? `No matching PO for ref "${r.statLine.supplierPoRef}".`
            : 'No PO reference on this line.');

      return {
        result: r,
        severity,
        matchedBills,
        hasPo: !!r.matchedPo,
        note,
        poNote,
        amountDelta: matchedBills[0] ? statAmt - matchedBills[0].totalIncGst : 0,
      };
    });
  }, [results, bills, supplierId]);

  // Derived counts for the summary band
  const findingCounts = useMemo(() => {
    const counts: Record<FindingSeverity, number> = {
      ok: 0, 'amount-mismatch': 0, 'duplicate-bill': 0, 'missing-bill': 0,
    };
    for (const f of findings) counts[f.severity]++;
    return counts;
  }, [findings]);

  // Severity filter for the findings table (v1.14.7).
  const [findingFilter, setFindingFilter] = useState<FindingSeverity | 'all' | 'issues'>('issues');
  const filteredFindings = useMemo(() => {
    if (findingFilter === 'all') return findings;
    if (findingFilter === 'issues') return findings.filter(f => f.severity !== 'ok');
    return findings.filter(f => f.severity === findingFilter);
  }, [findings, findingFilter]);

  // ── Persist reconciliation state back to the Statement entity ──
  // Marks status: Active → In Progress (first applied/skipped) → Completed (all lines resolved)
  const persistToStatement = useCallback((nextResults: MatchResult[]) => {
    if (!activeStatement) return;
    const lines = nextResults.map(r => ({
      id: r.statLine.id,
      supplierPoRef: r.statLine.supplierPoRef,
      supplierRef: r.statLine.supplierRef,
      project: r.statLine.project,
      jobRef: r.statLine.jobRef,
      description: r.statLine.description,
      amountIncGst: r.statLine.amountIncGst,
      matchedPoId: r.matchedPo?.id,
      matchStatus: r.status,
    }));
    const totalIncGst = nextResults.reduce((s, r) => s + r.statLine.amountIncGst, 0);
    updateStatement(activeStatement.id, {
      lines,
      invoiceCount: nextResults.length,
      totalIncGst,
      supplierId: supplierId || activeStatement.supplierId,
      statementDate: statementDate || activeStatement.statementDate,
      statementRef: statementRef || activeStatement.statementRef,
    });
  }, [activeStatement, updateStatement, supplierId, statementDate, statementRef]);

  // Auto-roll status when meaningful actions occur
  const bumpStatementStatus = useCallback((to: 'In Progress' | 'Completed') => {
    if (!activeStatement) return;
    if (activeStatement.status === to) return;
    if (activeStatement.status === 'Archived') return; // user controls archive
    setStatementStatus(activeStatement.id, to);
  }, [activeStatement, setStatementStatus]);

  // ── Hydrate from the active Statement entity (deep-link / drilled in from list) ──
  useEffect(() => {
    if (!activeStatement) return;
    if (hydratedRef.current === activeStatement.id) return;
    hydratedRef.current = activeStatement.id;
    if (activeStatement.supplierId) setSupplierId(activeStatement.supplierId);
    if (activeStatement.statementDate) setStatementDate(activeStatement.statementDate);
    if (activeStatement.statementRef)  setStatementRef(activeStatement.statementRef);

    // If the statement already has parsed lines, restore reconciliation state
    if (activeStatement.lines.length > 0) {
      const restored: StatementLine[] = activeStatement.lines.map(l => ({
        id: l.id,
        supplierPoRef: l.supplierPoRef,
        supplierRef: l.supplierRef,
        project: l.project,
        jobRef: l.jobRef,
        description: l.description,
        amountIncGst: l.amountIncGst,
      }));
      setProcessed(true);
      setProcessing(false);
      // Use a timeout so doMatch reads up-to-date PO refs
      setTimeout(() => doMatch(restored), 0);
    }
    // Update statement title in document
    document.title = `Reconcile · ${activeStatement.filename} · FieldInsight`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatement?.id]);

  // ── Consume pending upload (v1.14.6) ─────────────────────────────────────
  // When the user uploads a statement on /statements and chose "Verify" mode,
  // the file is stashed in pendingStatementUpload before navigation. Pick it
  // up here exactly once and route through the same parseFile() flow used
  // when the user drops a file directly.
  const consumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!activeStatement) return;
    if (consumedRef.current === activeStatement.id) return;
    if (activeStatement.lines.length > 0) return;     // already hydrated
    const file = consumePendingStatementUpload(activeStatement.id);
    if (!file) return;
    consumedRef.current = activeStatement.id;
    // Reuse the page's existing handleFile path (sets preview + kicks parse).
    handleFile(file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatement?.id]);

  // ── Tiered match logic ───────────────────────────────────────────────────────
  // Tier 1: exact PO id   Tier 2: project reference   Tier 3: job ref
  const doMatch = (lines: StatementLine[]) => {
    const pos = poRef.current;
    const matched: MatchResult[] = lines.map(l => {
      const statAmt = l.amountIncGst;
      const ref     = l.supplierPoRef.trim().toLowerCase();

      let po: PurchaseOrder | undefined;
      let matchedBy: MatchedBy | undefined;

      // Tier 1 — exact PO id
      po = pos.find(p => p.id.toLowerCase() === ref);
      if (po) matchedBy = 'po-id';

      // Tier 2 — project reference (po.reference)
      if (!po && ref) {
        po = pos.find(p => p.reference && p.reference.toLowerCase() === ref);
        if (po) matchedBy = 'project';
      }

      // Tier 3 — job reference (po.jobRef)
      if (!po && ref) {
        po = pos.find(p => p.jobRef && p.jobRef.toLowerCase() === ref);
        if (po) matchedBy = 'job-ref';
      }

      if (!po) {
        return { statLine: l, status: 'no-po' as MatchStatus, variance: statAmt, variancePct: 100, checked: false, newJobRef: '', newPostcode: '' };
      }

      const variance    = statAmt - po.total;
      const variancePct = po.total > 0 ? (variance / po.total) * 100 : (statAmt > 0 ? 100 : 0);
      let status: MatchStatus;
      if (po.total === 0 && statAmt > 0)                 status = 'zero-po';
      else if (Math.abs(variancePct) <= PRICE_MATCH_PCT) status = 'matched';
      else                                               status = 'amount-differs';
      const autoChecked = status === 'matched' || status === 'zero-po';
      return { statLine: l, status, matchedPo: po, matchedBy, variance, variancePct, checked: autoChecked, newJobRef: '', newPostcode: '' };
    });
    setResults(matched);
    setAppliedCount(0);
    setShowApplied(false);
    // Persist the parsed lines onto the Statement entity so the list shows
    // accurate invoice count / total and survives reload.
    persistToStatement(matched);
  };

  // ── Build demo lines from current POs (reads from ref) ──────────────────────
  const buildDemoLines = (): StatementLine[] => {
    const pos    = poRef.current;
    const zeroPo = pos.find(p => p.total === 0);
    const lines: StatementLine[] = [];
    let id = 1;
    const demoRefs = ['SUP-JOB-4401', 'INV-88234', 'DKT-99021', 'JOB-55190', 'REF-77832'];
    if (pos[0]) lines.push({ id: id++, supplierPoRef: pos[0].id, supplierRef: demoRefs[0],
      project: pos[0].reference || 'MEGA-01', jobRef: pos[0].jobRef || 'JOB-001',
      description: pos[0].notes?.split('\n')[0]?.slice(0, 40) || '', amountIncGst: pos[0].total });
    if (pos[1]) lines.push({ id: id++, supplierPoRef: pos[1].id, supplierRef: demoRefs[1],
      project: pos[1].reference || 'WOLF-040', jobRef: pos[1].jobRef || 'JOB-002',
      description: pos[1].notes?.split('\n')[0]?.slice(0, 40) || '', amountIncGst: parseFloat((pos[1].total * 1.18).toFixed(2)) });
    if (zeroPo) lines.push({ id: id++, supplierPoRef: zeroPo.id, supplierRef: demoRefs[2],
      project: zeroPo.reference || 'JEM-502', jobRef: zeroPo.jobRef || 'JOB-003',
      description: zeroPo.notes?.split('\n')[0]?.slice(0, 40) || '', amountIncGst: 247.50 });
    else if (pos[2]) lines.push({ id: id++, supplierPoRef: pos[2].id, supplierRef: demoRefs[2],
      project: pos[2].reference || 'SITE-42', jobRef: pos[2].jobRef || 'JOB-003',
      description: '', amountIncGst: pos[2].total });
    if (pos[3]) lines.push({ id: id++, supplierPoRef: pos[3].id, supplierRef: demoRefs[3],
      project: pos[3].reference || 'OMC-9594', jobRef: pos[3].jobRef || 'JOB-004',
      description: pos[3].notes?.split('\n')[0]?.slice(0, 40) || '', amountIncGst: pos[3].total });
    // Unmatched line — project/job ref from statement itself (no FI PO found)
    lines.push({ id: id++, supplierPoRef: 'PO-9991', supplierRef: demoRefs[4],
      project: 'PLANT-99', jobRef: 'JOB-9991',
      description: 'Unknown project ref', amountIncGst: 320.00 });
    return lines;
  };

  // ── Real parse via /api/parse-statement (Claude Vision) ─────────────────────
  const parseFile = (file: File | null) => {
    setProcessing(true);
    setProcessStep(0);
    setProcessed(false);
    setParseError(null);
    setAiFailure(null);

    // Animate progress steps while waiting for the API
    let step = 0;
    const interval = setInterval(() => {
      step = Math.min(step + 1, PROCESSING_STEPS.length - 2);
      setProcessStep(step);
    }, 900);

    const finish = (lines: StatementLine[], meta?: { supplierName?: string; statementDate?: string; statementRef?: string }) => {
      clearInterval(interval);
      setProcessStep(PROCESSING_STEPS.length - 1);
      setTimeout(() => {
        if (meta?.statementRef)  setStatementRef(prev => prev || meta.statementRef!);
        if (meta?.statementDate) setStatementDate(prev => prev || meta.statementDate!);
        if (meta?.supplierName) {
          // Try to auto-match supplier by name
          const sup = supRef.current.find(s =>
            s.name.toLowerCase().includes(meta.supplierName!.toLowerCase()) ||
            meta.supplierName!.toLowerCase().includes(s.name.toLowerCase()));
          if (sup) setSupplierId(prev => prev || sup.id);
        }
        if (!supplierId && poRef.current.length > 0) {
          setSupplierId(prev => prev || poRef.current[0].supplierId);
        }
        doMatch(lines);
        setProcessing(false);
        setProcessed(true);
      }, 400);
    };

    if (!file) {
      // Demo mode — no real file
      setDemoMode(true);
      finish(buildDemoLines(), { statementRef: 'STMT-2026-03' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    fetch('/api/parse-statement', { method: 'POST', body: formData })
      .then(async response => {
        if (response.ok) {
          const data = await response.json();
          // v1.15.3 — server may now return 200 with a structured error
          // body (it had to commit headers early to bypass Heroku's 30s
          // H12 timeout). Route those through the existing aiFailure path.
          if (data.code) {
            setAiFailure({ code: data.code, message: data.friendly ?? data.error, docsUrl: data.docsUrl });
            setDemoMode(true);
            finish(buildDemoLines(), { statementRef: 'STMT-2026-03' });
            return;
          }
          if (data.error) throw new Error(data.error);
          setDemoMode(false);
          finish(
            (data.lines || []).map((l: { id: number; supplierPoRef: string; supplierRef: string; project: string; jobRef: string; amountIncGst: number; description: string }, i: number) => ({
              id:            l.id ?? i + 1,
              supplierPoRef: l.supplierPoRef ?? '',
              supplierRef:   l.supplierRef   ?? '',
              project:       l.project       ?? '',
              jobRef:        l.jobRef        ?? '',
              amountIncGst:  l.amountIncGst,
              description:   l.description,
            })),
            { supplierName: data.supplierName, statementDate: data.statementDate, statementRef: data.statementRef },
          );
        } else {
          // Structured failure — capture code + friendly message and drop to demo
          const errBody = await response.json().catch(() => ({ error: 'Unknown server error' }));
          if (errBody.code) {
            setAiFailure({ code: errBody.code, message: errBody.friendly ?? errBody.error, docsUrl: errBody.docsUrl });
            setDemoMode(true);
            finish(buildDemoLines(), { statementRef: 'STMT-2026-03' });
            return;
          }
          if (response.status === 503) {
            setDemoMode(true);
            setParseError('API key not configured — showing demo data');
            finish(buildDemoLines(), { statementRef: 'STMT-2026-03' });
            return;
          }
          throw new Error(errBody.error ?? 'Parse failed');
        }
      })
      .catch(err => {
        clearInterval(interval);
        const msg = err instanceof Error ? err.message : 'Unknown error';
        setParseError(msg);
        setDemoMode(true);
        finish(buildDemoLines(), { statementRef: 'STMT-2026-03' });
      });
  };

  // ── File handlers ─────────────────────────────────────────────────────────────
  const handleFile = (file: File) => {
    setUploadedFile(file);
    setPdfUrl(URL.createObjectURL(file));
    parseFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleLoadDemo = () => {
    setUploadedFile(null);
    setPdfUrl(null);
    parseFile(null);
  };

  const deleteAll = () => {
    setUploadedFile(null);
    setPdfUrl(null);
    setProcessed(false);
    setProcessing(false);
    setResults([]);
    setShowApplied(false);
    setParseError(null);
  };

  // ── Toggle / apply ───────────────────────────────────────────────────────────
  const toggleChecked = (id: number) =>
    setResults(prev => prev.map(r => r.statLine.id === id ? { ...r, checked: !r.checked } : r));

  const updateResultField = (id: number, field: 'newJobRef' | 'newPostcode', value: string) =>
    setResults(prev => prev.map(r => r.statLine.id === id ? { ...r, [field]: value } : r));

  const applySelected = () => {
    let count = 0;
    for (const r of results) {
      if (!r.checked) continue;
      if (r.status === 'zero-po' && r.matchedPo) {
        const exGst = r.statLine.amountIncGst / 1.1;
        const gst   = r.statLine.amountIncGst - exGst;
        updatePurchaseOrder(r.matchedPo.id, {
          exGst: Math.round(exGst * 100) / 100,
          gst:   Math.round(gst  * 100) / 100,
          total: r.statLine.amountIncGst,
          status: 'All received',
        });
        count++;
      } else if (r.status === 'matched' && r.matchedPo) {
        updatePurchaseOrder(r.matchedPo.id, { status: 'All received' });
        count++;
      }
    }
    setAppliedCount(count);
    setShowApplied(true);
    setResults(prev => {
      const next = prev.map(r => {
        if (!r.checked) return r;
        if (r.status === 'zero-po' || r.status === 'matched') return { ...r, status: 'matched' as MatchStatus, variance: 0, variancePct: 0 };
        return r;
      });
      // Persist & bump status on the entity
      persistToStatement(next);
      const allResolved = next.every(r => r.status === 'matched');
      bumpStatementStatus(allResolved ? 'Completed' : 'In Progress');
      return next;
    });
  };

  // ── Apply manual links on top of auto-matched results ───────────────────────
  const displayResults: MatchResult[] = results.map(r => {
    const overridePoId = manualLinks[r.statLine.id];
    if (!overridePoId) return r;
    const po = purchaseOrders.find(p => p.id === overridePoId);
    if (!po) return r;
    const statAmt    = r.statLine.amountIncGst;
    const variance   = statAmt - po.total;
    const variancePct = po.total > 0 ? (variance / po.total) * 100 : (statAmt > 0 ? 100 : 0);
    let status: MatchStatus;
    if (po.total === 0 && statAmt > 0)                 status = 'zero-po';
    else if (Math.abs(variancePct) <= PRICE_MATCH_PCT) status = 'matched';
    else                                               status = 'amount-differs';
    return { ...r, matchedPo: po, matchedBy: 'po-id' as MatchedBy, status, variance, variancePct, checked: status === 'matched' || status === 'zero-po' };
  });

  const linkPo = (lineId: number, poId: string) =>
    setManualLinks(prev => ({ ...prev, [lineId]: poId }));

  // ── Derived ──────────────────────────────────────────────────────────────────
  const stats = {
    matched: displayResults.filter(r => r.status === 'matched').length,
    zeroPo:  displayResults.filter(r => r.status === 'zero-po').length,
    differs: displayResults.filter(r => r.status === 'amount-differs').length,
    noPo:    displayResults.filter(r => r.status === 'no-po').length,
  };
  const actionableSelected = displayResults.filter(r => r.checked && (r.status === 'matched' || r.status === 'zero-po')).length;
  const totalStatAmt = displayResults.reduce((s, r) => s + r.statLine.amountIncGst, 0);
  const totalPoAmt   = displayResults.reduce((s, r) => s + (r.matchedPo?.total ?? 0), 0);
  const differsRows  = displayResults.filter(r => r.status === 'amount-differs');

  // ── Resizable + collapsible left panel ───────────────────────────────────────
  const [leftWidth,     setLeftWidth]     = useState(310);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const containerRef    = useRef<HTMLDivElement>(null);
  const isDraggingPane  = useRef(false);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!isDraggingPane.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setLeftWidth(Math.max(200, Math.min(500, e.clientX - rect.left)));
    };
    const onUp = () => { isDraggingPane.current = false; document.body.style.cursor = ''; };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  return (
    <Box>
      {/* ── Toolbar ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button startIcon={<ArrowBackIcon />}
            onClick={() => navigate(activeStatement ? '/statements' : '/purchase-orders-list')}
            sx={{ textTransform: 'none', color: '#555', fontSize: '0.8rem' }}>
            {activeStatement ? 'Back to Statements' : 'Back to PO List'}
          </Button>
          <Typography sx={{ color: '#bbb', fontSize: '0.8rem' }}>›</Typography>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: BRAND }}>
            {activeStatement ? 'Verify Statement' : 'Statement Import'}
          </Typography>
          {activeStatement && (
            <>
              <Typography sx={{ color: '#bbb', fontSize: '0.8rem' }}>·</Typography>
              <Chip label={activeStatement.filename} size="small"
                sx={{ height: 22, fontSize: '0.7rem', backgroundColor: '#f5f0ff',
                  color: BRAND, fontWeight: 600, '& .MuiChip-label': { px: 0.8 } }} />
              <StatusChip status={activeStatement.status} />
              {activeStatement.supplierName && (
                <Typography sx={{ fontSize: '0.78rem', color: '#666' }}>
                  {activeStatement.supplierName}
                </Typography>
              )}
            </>
          )}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {/*
            v1.14.6 — Cross-link to the Import Bills workflow on the same
            statement. Distinguished by purple accent (Import Bills theme)
            vs the brand purple of this Verify page.
          */}
          {activeStatement && (
            <Tooltip title="Switch to Import Bills — fan each statement line out into a brand-new Bill" arrow>
              <Button size="small" variant="outlined"
                startIcon={<DescriptionIcon sx={{ fontSize: 14 }} />}
                onClick={() => navigate(`/bills/import?statement=${activeStatement.id}`)}
                sx={{ textTransform: 'none', fontSize: '0.72rem',
                  borderColor: '#6a1b9a', color: '#6a1b9a', fontWeight: 700,
                  '&:hover': { backgroundColor: '#f3e5f5', borderColor: '#4a148c' } }}>
                Switch to Import Bills
              </Button>
            </Tooltip>
          )}
          {!processed && !processing && (
            <Button variant="outlined" size="small" startIcon={<AutoFixHighIcon sx={{ fontSize: 14 }} />}
              onClick={handleLoadDemo}
              sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: BRAND, color: BRAND, fontWeight: 600 }}>
              Load Demo Statement
            </Button>
          )}
          {processed && !processing && (
            <Button variant="outlined" size="small"
              onClick={deleteAll}
              sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: '#ccc', color: '#666' }}>
              New Import
            </Button>
          )}
          {showApplied && appliedCount > 0 && (
            <Chip icon={<CheckCircleIcon sx={{ fontSize: 13 }} />}
              label={`${appliedCount} PO${appliedCount !== 1 ? 's' : ''} updated`}
              sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />
          )}
        </Stack>
      </Stack>

      {/* ── Split Panel ── */}
      <Box ref={containerRef} sx={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>

        {/* ── LEFT PANEL (sticky) ── */}
        <Box sx={{ width: leftCollapsed ? COLLAPSE_THUMB_WIDTH : leftWidth, flexShrink: 0, overflow: 'hidden', position: 'sticky', top: 16, transition: leftCollapsed ? 'width 0.18s ease' : 'none' }} className="no-print">
          {leftCollapsed ? (
            <DocumentPreviewRail
              collapsed
              fileName={uploadedFile?.name ?? (processed ? 'Statement' : 'Statement')}
              fileUrl={pdfUrl}
              fileType={uploadedFile?.type}
              demo={demoMode}
              thumbWidth={COLLAPSE_THUMB_WIDTH}
              expandedWidth={leftWidth}
              minHeight={320}
              onExpand={() => setLeftCollapsed(false)}
            />
          ) : (
          <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
            <input ref={fileInputRef} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={handleFileChange} />

            {/* Sub-tab header bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #eee', backgroundColor: '#fafafa', px: 0.5, minHeight: 36 }}>
              {(pdfUrl || processed) ? (
                <Stack direction="row" sx={{ flex: 1 }}>
                  <Box onClick={() => setSubTab('pdf')} sx={{ py: 0.7, px: 1.2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, borderBottom: subTab === 'pdf' ? `2px solid ${FI_ORANGE}` : '2px solid transparent' }}>
                    <PictureAsPdfIcon sx={{ fontSize: 12, color: subTab === 'pdf' ? FI_ORANGE : '#bbb' }} />
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: subTab === 'pdf' ? '#333' : '#999' }}>PDF Preview</Typography>
                  </Box>
                  <Box onClick={() => setSubTab('summary')} sx={{ py: 0.7, px: 1.2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.4, borderBottom: subTab === 'summary' ? `2px solid ${FI_BLUE}` : '2px solid transparent' }}>
                    <SummarizeIcon sx={{ fontSize: 12, color: subTab === 'summary' ? FI_BLUE : '#bbb' }} />
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: subTab === 'summary' ? '#333' : '#999' }}>Summary</Typography>
                  </Box>
                </Stack>
              ) : <Box sx={{ flex: 1 }} />}
              {(uploadedFile || processed) && (
                <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
                  <Button size="small" startIcon={<Sparkle size={11} variant="single" color={BRAND} />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ textTransform: 'none', fontSize: '0.64rem', color: BRAND, minWidth: 0, px: 1, py: 0.4 }}>
                    Re-read
                  </Button>
                  <Divider orientation="vertical" flexItem sx={{ my: 0.6 }} />
                  <Button size="small" startIcon={<DeleteSweepIcon sx={{ fontSize: 12 }} />}
                    onClick={deleteAll}
                    sx={{ textTransform: 'none', fontSize: '0.64rem', color: '#c62828', minWidth: 0, px: 1, py: 0.4 }}>
                    Delete
                  </Button>
                </Stack>
              )}
            </Box>

            {/* PDF sub-tab */}
            {subTab === 'pdf' && (
              <>
                {pdfUrl ? (
                  <Box
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    sx={{ height: 480, backgroundColor: '#fafafa', position: 'relative' }}>
                    {/*
                      v1.15.1 — Detect PDF by both MIME and filename. Some
                      browsers / drag sources (especially macOS iCloud Drive
                      and the pendingStatementUpload handoff) deliver Files
                      with type==='application/octet-stream' or '' — the old
                      strict equality check fell through to <img>, which
                      can't render a PDF blob and showed a broken icon.
                    */}
                    {(() => {
                      const fname = (uploadedFile?.name || '').toLowerCase();
                      const isPdf = uploadedFile?.type === 'application/pdf' || fname.endsWith('.pdf');
                      return isPdf ? (
                        <iframe src={pdfUrl} title="Statement preview" style={{ width: '100%', height: '100%', border: 'none' }} />
                      ) : (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                          <img src={pdfUrl} alt="Statement" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        </Box>
                      );
                    })()}
                  </Box>
                ) : !processed ? (
                  /* Upload zone */
                  <Box
                    onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      py: 6, textAlign: 'center', cursor: 'pointer',
                      border: `2px dashed ${isDragging ? BRAND : '#d0d0d0'}`,
                      backgroundColor: isDragging ? '#f5f0ff' : '#fafafa',
                      transition: 'all 0.2s', '&:hover': { borderColor: BRAND, backgroundColor: '#f5f0ff' },
                    }}>
                    <CloudUploadIcon sx={{ fontSize: 48, color: isDragging ? BRAND : '#ccc', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 700, color: '#555' }}>Drop supplier statement</Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: '#aaa', mt: 0.3 }}>PDF or image · Alfred extracts PO refs automatically</Typography>
                  </Box>
                ) : (
                  /* Demo mode — no PDF */
                  <Box sx={{ py: 6, textAlign: 'center', backgroundColor: '#faf8ff' }}>
                    <DescriptionIcon sx={{ fontSize: 40, color: `${BRAND}60`, mb: 1 }} />
                    <Typography sx={{ fontSize: '0.8rem', color: '#aaa' }}>Demo mode — no PDF uploaded</Typography>
                    <Button size="small" onClick={() => fileInputRef.current?.click()}
                      sx={{ mt: 1, textTransform: 'none', fontSize: '0.7rem', color: BRAND }}>
                      Upload real statement
                    </Button>
                  </Box>
                )}
              </>
            )}

            {/* Summary sub-tab */}
            {subTab === 'summary' && processed && (
              <Box sx={{ p: 2 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', mb: 1.5 }}>Statement Summary</Typography>
                <Stack spacing={1.3}>
                  {[
                    { label: 'Statement ref',         value: statementRef || '—' },
                    { label: 'Date',                   value: statementDate },
                    { label: 'Supplier',               value: supRef.current.find(s => s.id === supplierId)?.name || '—' },
                    { label: 'Lines',                  value: `${results.length}` },
                    { label: 'Statement total (inc GST)', value: fmt(totalStatAmt), bold: true },
                    { label: 'Our PO total',           value: fmt(totalPoAmt) },
                    { label: 'Variance',               value: `${totalStatAmt - totalPoAmt >= 0 ? '+' : ''}${fmt(totalStatAmt - totalPoAmt)}`, color: Math.abs(totalStatAmt - totalPoAmt) > 0.01 ? FI_ORANGE : '#2e7d32' },
                  ].map(row => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>{row.label}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: row.bold ? 800 : 600, color: (row as {color?: string}).color || '#333' }}>{row.value}</Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}

            {/* Parse progress (shown in left panel when processing) */}
            {processing && (
              <Box sx={{ p: 2 }}>
                <LinearProgress variant="determinate"
                  value={(processStep / (PROCESSING_STEPS.length - 1)) * 100}
                  sx={{ mb: 1.5 }} />
                {PROCESSING_STEPS.map((step, i) => (
                  <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    {i < processStep
                      ? <CheckCircleIcon sx={{ fontSize: 13, color: '#66bb6a' }} />
                      : i === processStep
                        ? <CircularProgress size={11} />
                        : <Box sx={{ width: 13, height: 13, borderRadius: '50%', border: '1.5px solid #e0e0e0' }} />
                    }
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: i === processStep ? 700 : 400,
                      color: i < processStep ? '#2e7d32' : i === processStep ? BRAND : '#bbb' }}>
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
          )}
        </Box>

        <CollapseDivider
          collapsed={leftCollapsed}
          onToggle={() => setLeftCollapsed(prev => !prev)}
          onDragStart={(e) => {
            if (!leftCollapsed) {
              isDraggingPane.current = true;
              document.body.style.cursor = 'col-resize';
              e.preventDefault();
            }
          }}
          expandLabel="Expand statement preview"
          collapseLabel="Collapse statement preview"
        />

        {/* ── RIGHT PANEL ── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

          {/* Before any parse: how-it-works */}
          {!processed && !processing && (
            <Paper sx={{ p: 3, border: `1px solid ${BRAND}30`, backgroundColor: '#faf8ff' }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <DescriptionIcon sx={{ color: BRAND, fontSize: 22, mt: 0.2 }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: BRAND }}>How Statement Import works</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: '#666', mt: 0.5, lineHeight: 1.7 }}>
                    Drop a supplier statement PDF on the left. Alfred parses each PO reference and amount, then matches
                    them against your existing POs — showing green for matches, blue for $0 POs ready to be set, amber
                    for amount differences that need manual review, and purple for references not found.
                    <br /><br />
                    <strong>Only $0 POs and exact matches are bulk-actionable.</strong> Any PO with a non-zero value
                    that differs from the statement must be reviewed individually from the PO screen.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Parsing spinner (right side) */}
          {processing && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
              <Stack alignItems="center" spacing={2}>
                <CircularProgress size={36} />
                <Typography sx={{ fontSize: '0.82rem', color: '#888' }}>Parsing statement…</Typography>
              </Stack>
            </Box>
          )}

          {/* Results */}
          {processed && !processing && (
            <>
              {demoMode && !parseError && !aiFailure && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.72rem' }}>
                  <strong>Demo mode</strong> — showing sample statement data. Drop a real statement PDF to auto-parse.
                </Alert>
              )}
              {aiFailure && (() => {
                const { code, message, docsUrl } = aiFailure;
                const titleByCode: Record<typeof code, string> = {
                  INSUFFICIENT_CREDITS: 'AI parsing paused — API credits exhausted',
                  RATE_LIMITED:         'AI parsing rate-limited',
                  OVERLOADED:           'AI service overloaded',
                  BAD_API_KEY:          'AI parsing key invalid',
                  UNKNOWN:              'AI parsing unavailable',
                };
                const sev: 'info' | 'warning' = code === 'INSUFFICIENT_CREDITS' ? 'info' : 'warning';
                return (
                  <Alert severity={sev} sx={{ mb: 2, fontSize: '0.72rem' }}
                    action={docsUrl ? (
                      <Button color="inherit" size="small" sx={{ fontSize: '0.65rem' }}
                        href={docsUrl} target="_blank" rel="noopener noreferrer">
                        Top up credits
                      </Button>
                    ) : undefined}>
                    <strong>{titleByCode[code]}</strong> — {message}
                  </Alert>
                );
              })()}
              {parseError && !aiFailure && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.72rem' }}>
                  <strong>Parse warning:</strong> {parseError} — showing demo data instead.
                </Alert>
              )}

              {/* Statement header */}
              <Paper sx={{ p: 2, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <FormControl size="small" fullWidth>
                    <InputLabel sx={{ fontSize: '0.8rem' }}>Supplier</InputLabel>
                    <Select value={supplierId} label="Supplier" onChange={e => setSupplierId(e.target.value)}
                      sx={{ fontSize: '0.82rem' }}>
                      {suppliers.map(s => (
                        <MenuItem key={s.id} value={s.id} sx={{ fontSize: '0.82rem' }}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Statement date" type="date" value={statementDate}
                    onChange={e => setStatementDate(e.target.value)}
                    InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                    inputProps={{ style: { fontSize: '0.82rem' } }} />
                  <TextField size="small" label="Statement reference" value={statementRef}
                    onChange={e => setStatementRef(e.target.value)}
                    placeholder="e.g. STMT-2026-03"
                    InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                    inputProps={{ style: { fontSize: '0.82rem' } }} />
                </Box>
              </Paper>

              {/*
                ── Sanity-check explainer (v1.14.7) ─────────────────────────
                Statement Verification is a READ-ONLY audit — we don't
                auto-reconcile because statements don't contain line-item
                detail, only invoice totals. This banner sets the user's
                expectation up front.
              */}
              <Alert severity="info" icon={<PlaylistAddCheckIcon fontSize="small" />}
                sx={{ mb: 2, fontSize: '0.74rem', '& .MuiAlert-message': { py: 0.4 } }}>
                <strong>Sanity check — do our Bills line up with the statement?</strong> For each statement line
                we look for a matching Bill in your records and check the amount. PO context is shown for follow-up
                only. <strong>No records are modified by this view.</strong> For bulk creation of new Bills, switch to <em>Import Bills</em>.
              </Alert>

              {/* Severity summary band — four at-a-glance counts (Bill-centric) */}
              <Stack direction="row" spacing={1.5} mb={2} flexWrap="wrap" alignItems="center">
                {(['ok', 'amount-mismatch', 'duplicate-bill', 'missing-bill'] as FindingSeverity[]).map(sev => {
                  const cfg = SEVERITY_CFG[sev];
                  const count = findingCounts[sev];
                  const active = findingFilter === sev;
                  return (
                    <Chip
                      key={sev}
                      label={`${count} ${cfg.label}`}
                      size="small"
                      variant={active ? 'filled' : 'outlined'}
                      onClick={() => setFindingFilter(active ? 'issues' : sev)}
                      sx={{
                        backgroundColor: active ? cfg.bg : 'transparent',
                        color: cfg.color, borderColor: cfg.border, fontWeight: 700, fontSize: '0.68rem',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: cfg.bg },
                      }} />
                  );
                })}
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: '0.7rem', color: '#aaa' }}>
                  Statement total: <strong style={{ color: BRAND }}>{fmt(totalStatAmt)}</strong>
                </Typography>
              </Stack>

              {/* Filter toggle: show all lines vs only issues */}
              <Stack direction="row" spacing={1} mb={1.2} alignItems="center">
                <Typography sx={{ fontSize: '0.7rem', color: '#888', fontWeight: 600 }}>Show:</Typography>
                <Chip
                  label={`Issues only · ${findings.length - findingCounts.ok}`}
                  size="small"
                  onClick={() => setFindingFilter('issues')}
                  sx={{
                    backgroundColor: findingFilter === 'issues' ? BRAND : '#f5f0ff',
                    color: findingFilter === 'issues' ? '#fff' : BRAND,
                    fontWeight: 700, fontSize: '0.65rem', cursor: 'pointer',
                  }} />
                <Chip
                  label={`All lines · ${findings.length}`}
                  size="small"
                  onClick={() => setFindingFilter('all')}
                  sx={{
                    backgroundColor: findingFilter === 'all' ? BRAND : '#f5f0ff',
                    color: findingFilter === 'all' ? '#fff' : BRAND,
                    fontWeight: 700, fontSize: '0.65rem', cursor: 'pointer',
                  }} />
                {(findingFilter !== 'issues' && findingFilter !== 'all') && (
                  <Chip
                    label={`Filter: ${SEVERITY_CFG[findingFilter].label}`}
                    size="small"
                    onDelete={() => setFindingFilter('issues')}
                    sx={{ fontSize: '0.65rem', backgroundColor: '#f5f0ff', color: BRAND, fontWeight: 700 }} />
                )}
              </Stack>

              {/* ── Findings table (v1.14.7 — replaces the legacy
                  Reconciliation table + Apply bar) ───────────────────────── */}
              <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden', mb: 2 }}>
                <Box sx={{ px: 2, py: 0.8, backgroundColor: '#f5f0ff', borderBottom: '1px solid #e8e0f5',
                  display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: BRAND }}>Findings</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                    {filteredFindings.length} of {findings.length} line{findings.length !== 1 ? 's' : ''} shown
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    {/* ── Row 1: group banners — Statement vs Our Bill ── */}
                    <TableRow>
                      <TableCell sx={{ width: 108, borderBottom: 0, backgroundColor: '#fafafa' }} />
                      <TableCell colSpan={2} sx={{ borderBottom: '1px solid #eee', backgroundColor: '#fafafa',
                        py: 0.4, fontWeight: 800, fontSize: '0.6rem', color: '#888',
                        textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Statement says
                      </TableCell>
                      <TableCell colSpan={3} sx={{ borderBottom: '1px solid #d9c6f5', backgroundColor: '#f7f0ff',
                        py: 0.4, fontWeight: 800, fontSize: '0.6rem', color: BRAND,
                        textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Our Bill on file
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0, backgroundColor: '#fafafa', py: 0.4,
                        fontWeight: 800, fontSize: '0.6rem', color: '#888',
                        textTransform: 'uppercase', letterSpacing: 0.8 }}>
                        Context
                      </TableCell>
                      <TableCell sx={{ borderBottom: 0, backgroundColor: '#fafafa', width: 200 }} />
                    </TableRow>
                    {/* ── Row 2: column labels ── */}
                    <TableRow sx={{ '& .MuiTableCell-root': { py: 0.5, fontWeight: 700,
                      fontSize: '0.6rem', color: '#777', textTransform: 'uppercase', letterSpacing: 0.4 } }}>
                      <TableCell sx={{ width: 108 }}>Finding</TableCell>
                      <TableCell sx={{ width: 110 }}>Invoice #</TableCell>
                      <TableCell align="right" sx={{ width: 80 }}>Amount</TableCell>
                      <TableCell sx={{ width: 110, backgroundColor: '#fcfaff' }}>Bill #</TableCell>
                      <TableCell align="right" sx={{ width: 80, backgroundColor: '#fcfaff' }}>Bill $</TableCell>
                      <TableCell align="right" sx={{ width: 70, backgroundColor: '#fcfaff' }}>Δ</TableCell>
                      <TableCell>PO · Job</TableCell>
                      <TableCell sx={{ width: 200 }}>Follow up</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredFindings.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 4, textAlign: 'center', color: '#aaa', fontSize: '0.78rem' }}>
                          {findingFilter === 'issues' && findingCounts.ok === findings.length
                            ? 'All clear — every supplier invoice on this statement matches a Bill in your records.'
                            : 'No findings match the current filter.'}
                        </TableCell>
                      </TableRow>
                    ) : filteredFindings.map(f => {
                      const cfg = SEVERITY_CFG[f.severity];
                      const r   = f.result;
                      const bill = f.matchedBills.length === 1 ? f.matchedBills[0] : undefined;
                      const dup  = f.matchedBills.length > 1;
                      const dupTotal = dup ? f.matchedBills.reduce((s, b) => s + b.totalIncGst, 0) : 0;
                      return (
                        <TableRow key={r.statLine.id} hover sx={{ '& .MuiTableCell-root': { py: 0.5 } }}>
                          {/* ── Finding chip ── */}
                          <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4,
                              px: 0.7, py: 0.2, borderRadius: 1,
                              backgroundColor: cfg.bg, border: `1px solid ${cfg.border}` }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: cfg.color }} />
                              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>
                                {cfg.label}
                              </Typography>
                            </Box>
                          </TableCell>
                          {/* ── STATEMENT SIDE ── */}
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#444', fontWeight: 700 }}>
                            {r.statLine.supplierRef || <span style={{ color: '#ccc' }}>—</span>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#333' }}>
                            {fmt(r.statLine.amountIncGst)}
                          </TableCell>
                          {/* ── BILL SIDE (primary check) ── */}
                          <TableCell sx={{ backgroundColor: '#fcfaff' }}>
                            {bill ? (
                              <Box component="span"
                                onClick={() => navigate(`/bills/${bill.id}`)}
                                sx={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 700,
                                  color: BRAND, cursor: 'pointer', textDecoration: 'underline',
                                  textDecorationColor: 'transparent',
                                  '&:hover': { textDecorationColor: BRAND } }}>
                                {bill.billNo}
                              </Box>
                            ) : dup ? (
                              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.66rem',
                                fontWeight: 700, color: '#b71c1c' }}>
                                {f.matchedBills.length} bills
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: '0.66rem', fontStyle: 'italic',
                                color: '#9c27b0', fontWeight: 700 }}>
                                — none —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#fcfaff',
                            fontSize: '0.7rem', fontWeight: 600, color: '#444' }}>
                            {bill ? fmt(bill.totalIncGst) : dup ? fmt(dupTotal) : <span style={{ color: '#ccc' }}>—</span>}
                          </TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#fcfaff' }}>
                            {f.severity === 'amount-mismatch' ? (
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700,
                                color: f.amountDelta > 0 ? '#c62828' : '#0277bd', whiteSpace: 'nowrap' }}>
                                {f.amountDelta > 0 ? '+' : ''}{fmt(f.amountDelta)}
                              </Typography>
                            ) : f.severity === 'ok' ? (
                              <Typography sx={{ fontSize: '0.7rem', color: '#2e7d32', fontWeight: 700 }}>
                                ✓
                              </Typography>
                            ) : (
                              <Typography sx={{ fontSize: '0.7rem', color: '#ccc' }}>—</Typography>
                            )}
                          </TableCell>
                          {/* ── CONTEXT (PO, Job, note) ── */}
                          <TableCell sx={{ fontSize: '0.65rem', color: '#666', lineHeight: 1.45 }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                              {r.matchedPo ? (
                                <Box component="span"
                                  onClick={() => navigate(`/po-print/${r.matchedPo!.id}`)}
                                  sx={{ color: FI_ORANGE, fontFamily: 'monospace', fontWeight: 700,
                                    cursor: 'pointer', textDecoration: 'underline',
                                    textDecorationColor: 'transparent',
                                    '&:hover': { textDecorationColor: FI_ORANGE } }}>
                                  {r.matchedPo.id}
                                </Box>
                              ) : (
                                <Tooltip title={f.poNote}>
                                  <Typography component="span" sx={{ fontSize: '0.62rem',
                                    fontStyle: 'italic', color: '#bbb', whiteSpace: 'nowrap' }}>
                                    no PO
                                  </Typography>
                                </Tooltip>
                              )}
                              {(r.statLine.jobRef || r.matchedPo?.jobRef) && (
                                <Typography component="span" sx={{ fontFamily: 'monospace',
                                  fontSize: '0.62rem', color: '#888' }}>
                                  · {r.statLine.jobRef || r.matchedPo?.jobRef}
                                </Typography>
                              )}
                            </Stack>
                            <Typography sx={{ fontSize: '0.6rem', color: '#999', mt: 0.2,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {f.note}
                            </Typography>
                          </TableCell>
                          {/* ── Follow-up actions ── */}
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {f.severity === 'ok' && (
                                <Typography sx={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic' }}>
                                  Nothing to do
                                </Typography>
                              )}
                              {f.severity === 'missing-bill' && (
                                <>
                                  {r.matchedPo && (
                                    <Tooltip title={`Open ${r.matchedPo.id}`}>
                                      <Button size="small" variant="outlined"
                                        onClick={() => navigate(`/po-print/${r.matchedPo!.id}`)}
                                        startIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                                        sx={{ textTransform: 'none', fontSize: '0.6rem', py: 0.2, px: 0.7,
                                          borderColor: FI_ORANGE, color: FI_ORANGE }}>
                                        Open PO
                                      </Button>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Import this invoice as a new Bill via the Bill Importer">
                                    <Button size="small" variant="contained"
                                      onClick={() => navigate(`/bills/import?statement=${activeStatement?.id ?? ''}`)}
                                      sx={{ textTransform: 'none', fontSize: '0.6rem', py: 0.2, px: 0.7,
                                        backgroundColor: '#9c27b0',
                                        '&:hover': { backgroundColor: '#6a1b9a' } }}>
                                      Import bill
                                    </Button>
                                  </Tooltip>
                                </>
                              )}
                              {f.severity === 'duplicate-bill' && (
                                <>
                                  {f.matchedBills.slice(0, 2).map(b => (
                                    <Tooltip key={b.id} title={`Open ${b.billNo}`}>
                                      <Button size="small" variant="outlined"
                                        onClick={() => navigate(`/bills/${b.id}`)}
                                        startIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                                        sx={{ textTransform: 'none', fontSize: '0.6rem', py: 0.2, px: 0.7,
                                          borderColor: '#ef9a9a', color: '#b71c1c' }}>
                                        {b.billNo}
                                      </Button>
                                    </Tooltip>
                                  ))}
                                  {f.matchedBills.length > 2 && (
                                    <Typography sx={{ fontSize: '0.6rem', color: '#b71c1c', fontWeight: 700 }}>
                                      +{f.matchedBills.length - 2} more
                                    </Typography>
                                  )}
                                </>
                              )}
                              {f.severity === 'amount-mismatch' && bill && (
                                <Tooltip title={`Open Bill ${bill.billNo} to investigate the amount difference`}>
                                  <Button size="small" variant="outlined"
                                    onClick={() => navigate(`/bills/${bill.id}`)}
                                    startIcon={<OpenInNewIcon sx={{ fontSize: '10px !important' }} />}
                                    sx={{ textTransform: 'none', fontSize: '0.6rem', py: 0.2, px: 0.7,
                                      borderColor: '#ffcc80', color: '#e65100' }}>
                                    Investigate
                                  </Button>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>

              {/* Footer: re-run / re-import action only — no mass apply on a sanity check */}
              <Paper sx={{ p: 1.5, border: '1px solid #e0e0e0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#888' }}>
                  Read-only audit — no records were modified by this view.
                </Typography>
                <Button variant="outlined" size="small" onClick={deleteAll}
                  sx={{ textTransform: 'none', borderColor: '#ccc', color: '#666', fontSize: '0.72rem' }}>
                  Re-upload statement
                </Button>
              </Paper>

              {/* Legacy reconciliation table — preserved but hidden in v1.14.7.
                  Kept commented-out as the user may want it back; the
                  sanity-check view above is the new default. */}
              <Paper sx={{ display: 'none', border: '1px solid #e0e0e0', overflow: 'hidden', mb: 2 }}>
                {/* Title bar — no directional text, headings are in the table */}
                <Box sx={{ px: 2, py: 0.8, backgroundColor: '#f5f0ff', borderBottom: '1px solid #e8e0f5', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: BRAND }}>Reconciliation</Typography>
                  {supplierId && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#555' }}>
                      — {suppliers.find(s => s.id === supplierId)?.name ?? supplierId}
                      {statementRef ? ` · Stmt ${statementRef}` : ''}
                      {statementDate ? ` · ${new Date(statementDate + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                    </Typography>
                  )}
                </Box>

                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    {/* ── Row 1: group headings ── */}
                    <TableRow>
                      {/* checkbox spacer */}
                      <TableCell padding="checkbox" sx={{ width: 32, backgroundColor: '#fefcff', borderBottom: 0, pb: 0 }} />
                      {/* Our PO group heading — spans Internal PO, Project, Job #, Status, PO Value */}
                      <TableCell colSpan={5}
                        sx={{ fontWeight: 800, fontSize: '0.65rem', color: BRAND, textTransform: 'uppercase',
                          letterSpacing: 0.8, backgroundColor: '#fefcff', borderBottom: '1px solid #ede5ff',
                          borderRight: '2px solid #e0e0e0', pb: 0.3, pt: 0.6 }}>
                        Our PO
                      </TableCell>
                      {/* Statement group heading — spans Stmt Ref, Statement PO, Stmt Amt, Var, Action, link */}
                      <TableCell colSpan={6}
                        sx={{ fontWeight: 800, fontSize: '0.65rem', color: '#555', textTransform: 'uppercase',
                          letterSpacing: 0.8, backgroundColor: '#fafafa', borderBottom: '1px solid #e8e8e8',
                          pb: 0.3, pt: 0.6 }}>
                        Statement
                      </TableCell>
                    </TableRow>

                    {/* ── Row 2: column labels ── */}
                    <TableRow sx={{ '& .MuiTableCell-root': { py: 0.5, pt: 0.3 } }}>
                      {/* Our PO side */}
                      <TableCell padding="checkbox" sx={{ width: 32, pl: 1, backgroundColor: '#fefcff', borderRight: '1px solid #f0e8ff' }} />
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 88, backgroundColor: '#fefcff' }}>
                        <Tooltip title="Your internal FieldInsight PO number"><span>Internal PO</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 78, backgroundColor: '#fefcff' }}>
                        <Tooltip title="Project reference on the FieldInsight PO"><span>Project</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 72, backgroundColor: '#fefcff' }}>
                        <Tooltip title="Job reference on the FieldInsight PO"><span>Job #</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 84, backgroundColor: '#fefcff' }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 74, backgroundColor: '#fefcff', borderRight: '2px solid #e0e0e0' }}>
                        <Tooltip title="Current FieldInsight PO value"><span>PO Value</span></Tooltip>
                      </TableCell>
                      {/* Statement side */}
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 90, backgroundColor: '#fafafa' }}>
                        <Tooltip title="Supplier's own internal job/docket/invoice reference"><span>Stmt Ref</span></Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 90, backgroundColor: '#fafafa' }}>
                        <Tooltip title="The PO reference the supplier recorded on the statement (should match your Internal PO)"><span>Statement PO</span></Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 74, backgroundColor: '#fafafa' }}>Stmt Amt</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', width: 66, backgroundColor: '#fafafa' }}>Var</TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.61rem', color: '#888', textTransform: 'uppercase', backgroundColor: '#fafafa', width: 170 }}>Action</TableCell>
                      <TableCell padding="checkbox" sx={{ width: 28, backgroundColor: '#fafafa' }} />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {results.map(r => {
                      const cfg = STATUS_CONFIG[r.status];
                      const canBulk = r.status === 'matched' || r.status === 'zero-po';
                      const supplierName = r.matchedPo
                        ? suppliers.find(s => s.id === r.matchedPo!.supplierId)?.name ?? ''
                        : '';
                      const rowBg =
                        r.status === 'matched'        ? '#f7fff8' :
                        r.status === 'zero-po'        ? '#f0f7ff' :
                        r.status === 'amount-differs' ? '#fffbf5' :
                        r.status === 'no-po'          ? '#fdf4ff' : undefined;
                      return (
                        <TableRow key={r.statLine.id} hover
                          sx={{ backgroundColor: rowBg, '& .MuiTableCell-root': { py: 0.35 } }}>

                          {/* Checkbox */}
                          <TableCell padding="checkbox" sx={{ pl: 1, borderRight: '1px solid #f5f0ff' }}>
                            {canBulk ? (
                              <Checkbox size="small" checked={r.checked} onChange={() => toggleChecked(r.statLine.id)}
                                sx={{ p: 0.2, color: r.status === 'matched' ? '#a5d6a7' : '#90caf9' }} />
                            ) : <Box sx={{ width: 22 }} />}
                          </TableCell>

                          {/* Internal PO — searchable Autocomplete for no-po rows */}
                          <TableCell sx={{ overflow: 'visible', p: r.status === 'no-po' ? 0.4 : undefined }}>
                            {r.status === 'no-po' ? (
                              <Autocomplete
                                size="small"
                                options={purchaseOrders}
                                getOptionLabel={po => po.id}
                                renderOption={(props, po) => {
                                  const sName = suppliers.find(s => s.id === po.supplierId)?.name ?? '';
                                  return (
                                    <li {...props} key={po.id} style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
                                      <strong>{po.id}</strong>
                                      {sName ? <span style={{ color: '#888', marginLeft: 6 }}>{sName}</span> : null}
                                      {po.jobRef ? <span style={{ color: '#aaa', marginLeft: 6 }}>· {po.jobRef}</span> : null}
                                    </li>
                                  );
                                }}
                                onChange={(_e, po) => po && linkPo(r.statLine.id, po.id)}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Search PO…"
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem' },
                                          '& .MuiOutlinedInput-root': { height: 26 } }} />
                                )}
                                sx={{ minWidth: 110 }}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                              />
                            ) : (
                              <Tooltip title={supplierName} placement="right">
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, fontFamily: 'monospace',
                                  color: FI_ORANGE, whiteSpace: 'nowrap' }}>
                                  {r.matchedPo!.id}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>

                          {/* Project — searchable by po.reference for no-po rows */}
                          <TableCell sx={{ overflow: 'visible', p: r.status === 'no-po' ? 0.4 : undefined }}>
                            {r.status === 'no-po' ? (
                              <Autocomplete
                                size="small"
                                options={Array.from(new Set(purchaseOrders.map(p => p.reference).filter(Boolean))) as string[]}
                                onChange={(_e, val) => {
                                  if (!val) return;
                                  const po = purchaseOrders.find(p => p.reference === val);
                                  if (po) linkPo(r.statLine.id, po.id);
                                }}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Project…"
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem' },
                                          '& .MuiOutlinedInput-root': { height: 26 } }} />
                                )}
                                sx={{ minWidth: 90 }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: '0.67rem', color: '#555', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.matchedPo?.reference || r.statLine.project
                                  ? <span style={{ color: r.matchedPo?.reference ? '#555' : '#888' }}>{r.matchedPo?.reference || r.statLine.project}</span>
                                  : <span style={{ color: '#ccc' }}>—</span>}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Job # — searchable by po.jobRef for no-po rows */}
                          <TableCell sx={{ overflow: 'visible', p: r.status === 'no-po' ? 0.4 : undefined }}>
                            {r.status === 'no-po' ? (
                              <Autocomplete
                                size="small"
                                options={Array.from(new Set(purchaseOrders.map(p => p.jobRef).filter(Boolean))) as string[]}
                                onChange={(_e, val) => {
                                  if (!val) return;
                                  const po = purchaseOrders.find(p => p.jobRef === val);
                                  if (po) linkPo(r.statLine.id, po.id);
                                }}
                                renderInput={params => (
                                  <TextField {...params} placeholder="Job #…"
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.7rem' },
                                          '& .MuiOutlinedInput-root': { height: 26 } }} />
                                )}
                                sx={{ minWidth: 80 }}
                              />
                            ) : (
                              <Typography sx={{ fontSize: '0.67rem', color: '#555', whiteSpace: 'nowrap',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {r.matchedPo?.jobRef || r.statLine.jobRef
                                  ? <span style={{ color: r.matchedPo?.jobRef ? '#555' : '#888' }}>{r.matchedPo?.jobRef || r.statLine.jobRef}</span>
                                  : <span style={{ color: '#ccc' }}>—</span>}
                              </Typography>
                            )}
                          </TableCell>

                          {/* Status */}
                          <TableCell>
                            <Stack spacing={0.2}>
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                px: 0.7, py: 0.15, borderRadius: 1, backgroundColor: cfg.bg }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: cfg.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.59rem', fontWeight: 700, color: cfg.color, whiteSpace: 'nowrap' }}>
                                  {cfg.label}
                                </Typography>
                              </Box>
                              {r.matchedBy && r.matchedBy !== 'po-id' && (
                                <Typography sx={{ fontSize: '0.55rem', color: '#aaa', pl: 0.2 }}>
                                  via {r.matchedBy === 'project' ? 'Project #' : 'Job #'}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>

                          {/* PO Value */}
                          <TableCell align="right" sx={{ borderRight: '2px solid #e8e8e8', fontSize: '0.73rem', fontWeight: 600,
                            color: r.status === 'zero-po' ? '#bbb' : '#444' }}>
                            {r.matchedPo ? fmt(r.matchedPo.total) : '—'}
                          </TableCell>

                          {/* Stmt Ref — supplier's own internal ref */}
                          <TableCell sx={{ overflow: 'hidden' }}>
                            <Typography sx={{ fontSize: '0.67rem', color: '#666', fontFamily: 'monospace',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.statLine.supplierRef || <span style={{ color: '#ccc' }}>—</span>}
                            </Typography>
                          </TableCell>

                          {/* Statement PO — what the supplier recorded as our PO ref */}
                          <TableCell sx={{ overflow: 'hidden' }}>
                            <Typography sx={{ fontSize: '0.67rem', fontFamily: 'monospace',
                              fontWeight: r.status === 'no-po' ? 700 : 400,
                              color: r.status === 'no-po' ? '#9c27b0' : '#888',
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {r.statLine.supplierPoRef || <span style={{ color: '#ccc' }}>—</span>}
                            </Typography>
                          </TableCell>

                          {/* Statement amount */}
                          <TableCell align="right">
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 800,
                              color: r.status === 'matched' ? '#2e7d32' : r.status === 'no-po' ? '#9c27b0' : BRAND }}>
                              {fmt(r.statLine.amountIncGst)}
                            </Typography>
                          </TableCell>

                          {/* Variance */}
                          <TableCell align="right">
                            {r.status !== 'matched' && r.status !== 'no-po' ? (
                              <Typography sx={{ fontSize: '0.67rem', fontWeight: 700,
                                color: r.variance > 0 ? '#c62828' : '#0277bd', whiteSpace: 'nowrap' }}>
                                {r.variance > 0 ? '+' : ''}{fmt(r.variance)}
                              </Typography>
                            ) : <Typography sx={{ fontSize: '0.62rem', color: '#ccc' }}>—</Typography>}
                          </TableCell>
                          {/* Action — full-width vertical stack */}
                          <TableCell sx={{ overflow: 'hidden', pr: 1 }}>
                            {r.status === 'matched' && (
                              <Tooltip title="Tick the checkbox to include — will be marked Received when you Apply">
                                <Typography sx={{ fontSize: '0.62rem', color: '#aaa', fontStyle: 'italic', cursor: 'default' }}>
                                  Mark received
                                </Typography>
                              </Tooltip>
                            )}
                            {r.status === 'zero-po' && (
                              <Tooltip title="Tick the checkbox to include — PO value will be set to the statement amount and marked Received">
                                <Typography sx={{ fontSize: '0.65rem', color: '#0277bd', fontWeight: 600, cursor: 'default' }}>
                                  Set → {fmt(r.statLine.amountIncGst)}
                                </Typography>
                              </Tooltip>
                            )}
                            {r.status === 'amount-differs' && (
                              <Stack direction="column" spacing={0.5} alignItems="flex-start">
                                <Tooltip title={`Open ${r.matchedPo?.id} in the PO screen to manually reconcile the amount difference`}>
                                  <Button size="small" variant="outlined"
                                    onClick={() => r.matchedPo && navigate(`/po-print/${r.matchedPo.id}`)}
                                    endIcon={<OpenInNewIcon sx={{ fontSize: 10 }} />}
                                    sx={{ textTransform: 'none', fontSize: '0.67rem', py: 0.45,
                                      whiteSpace: 'nowrap',
                                      borderColor: '#ffcc80', color: '#e65100', justifyContent: 'flex-start',
                                      '&:hover': { borderColor: '#e65100', backgroundColor: '#fff8f0' } }}>
                                    Review on PO
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Add the statement amount as a new MISC line on this FieldInsight PO, using the supplier reference in the description">
                                  <Button size="small" variant="outlined"
                                    onClick={() => { setAddLineTargetPo(r.matchedPo?.id ?? ''); setAddLineCode(''); setAddLineDialog(r); }}
                                    sx={{ textTransform: 'none', fontSize: '0.67rem', py: 0.45,
                                      whiteSpace: 'nowrap',
                                      borderColor: '#c8e6c9', color: '#2e7d32', justifyContent: 'flex-start',
                                      '&:hover': { borderColor: '#2e7d32', backgroundColor: '#f1f8e9' } }}>
                                    + Stmt Amt to PO Line
                                  </Button>
                                </Tooltip>
                              </Stack>
                            )}
                            {r.status === 'no-po' && (
                              <Stack direction="column" spacing={0.5} alignItems="flex-start">
                                <Tooltip title="Create a brand-new FieldInsight PO for this statement reference">
                                  <Button size="small" variant="outlined"
                                    onClick={() => setCreatePoDialog(r)}
                                    sx={{ textTransform: 'none', fontSize: '0.67rem', py: 0.45,
                                      whiteSpace: 'nowrap',
                                      borderColor: '#ce93d8', color: '#6a1b9a', justifyContent: 'flex-start',
                                      '&:hover': { borderColor: '#6a1b9a', backgroundColor: '#fce4ec' } }}>
                                    Create PO
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Create a new PO and link it to an existing job reference">
                                  <Button size="small" variant="outlined"
                                    onClick={() => setCreatePoDialog(r)}
                                    sx={{ textTransform: 'none', fontSize: '0.67rem', py: 0.45,
                                      whiteSpace: 'nowrap',
                                      borderColor: '#9fa8da', color: '#283593', justifyContent: 'flex-start',
                                      '&:hover': { borderColor: '#283593', backgroundColor: '#e8eaf6' } }}>
                                    Add new PO
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Add the statement amount as a new MISC line on an existing FieldInsight PO">
                                  <Button size="small" variant="outlined"
                                    onClick={() => { setAddLineTargetPo(purchaseOrders[0]?.id ?? ''); setAddLineCode(''); setAddLineDialog(r); }}
                                    sx={{ textTransform: 'none', fontSize: '0.67rem', py: 0.45,
                                      whiteSpace: 'nowrap',
                                      borderColor: '#c8e6c9', color: '#2e7d32', justifyContent: 'flex-start',
                                      '&:hover': { borderColor: '#2e7d32', backgroundColor: '#f1f8e9' } }}>
                                    + Stmt Amt to PO Line
                                  </Button>
                                </Tooltip>
                              </Stack>
                            )}
                          </TableCell>
                          {/* Open link */}
                          <TableCell padding="checkbox" sx={{ pr: 0.5 }}>
                            {r.matchedPo && (
                              <Tooltip title={`Open ${r.matchedPo.id}`}>
                                <IconButton size="small" onClick={() => navigate(`/po-print/${r.matchedPo!.id}`)}>
                                  <OpenInNewIcon sx={{ fontSize: 11, color: '#ccc' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>

              {/* Needs Review callout (legacy, v1.14.7 — replaced by Findings band; preserved guarded by `false` for now) */}
              {false && differsRows.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2, fontSize: '0.72rem' }}>
                  <strong>{differsRows.length} PO{differsRows.length !== 1 ? 's' : ''} need manual review</strong> — amounts
                  don't match the statement. Open each PO and use "New Bill Received" to reconcile properly.&nbsp;
                  {differsRows.map((r, i) => (
                    <React.Fragment key={r.statLine.id}>
                      {i > 0 && ' · '}
                      <Box component="span"
                        onClick={() => r.matchedPo && navigate(`/po-print/${r.matchedPo.id}`)}
                        sx={{ textDecoration: 'underline', cursor: 'pointer', fontWeight: 700 }}>
                        {r.statLine.supplierPoRef} ↗
                      </Box>
                    </React.Fragment>
                  ))}
                </Alert>
              )}

              {/* Apply bar (legacy, v1.14.7 — Verify is now read-only; preserved hidden) */}
              <Paper sx={{ display: 'none', p: 2, border: '1px solid #e0e0e0', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#333' }}>
                    {actionableSelected} action{actionableSelected !== 1 ? 's' : ''} selected
                  </Typography>
                  <Typography sx={{ fontSize: '0.68rem', color: '#888' }}>
                    {actionableSelected > 0
                      ? 'Ticked POs will be marked received ($0 POs will also have their amount set from the statement).'
                      : differsRows.length > 0
                        ? 'Amount-differs POs must be reviewed individually via the "Review on PO" button above.'
                        : 'No updates to apply — all POs matched.'}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button variant="outlined" size="small" onClick={deleteAll}
                    sx={{ textTransform: 'none', borderColor: '#ccc', color: '#666' }}>
                    New Import
                  </Button>
                  <Button variant="contained" size="small"
                    startIcon={<PlaylistAddCheckIcon />}
                    disabled={actionableSelected === 0}
                    onClick={applySelected}
                    sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700,
                      '&:hover': { backgroundColor: '#4a3580' },
                      '&.Mui-disabled': { backgroundColor: '#e0d5f5', color: BRAND } }}>
                    Apply {actionableSelected > 0 ? `${actionableSelected} Update${actionableSelected !== 1 ? 's' : ''}` : 'Updates'}
                  </Button>
                </Stack>
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* ── Add Supplier PO Line Dialog ── */}
      <Dialog open={!!addLineDialog} onClose={() => setAddLineDialog(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 34, height: 34, borderRadius: '50%', backgroundColor: '#e8f5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PlaylistAddCheckIcon sx={{ fontSize: 18, color: '#2e7d32' }} />
            </Box>
            <Box>
              <Typography fontWeight={800}>Add Supplier PO Line</Typography>
              <Typography variant="caption" color="text.secondary">
                Add this supplier charge as a new line on an existing FieldInsight PO
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {addLineDialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* Statement summary */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                <Box sx={{ p: 1.5, backgroundColor: '#fafafa', borderRadius: 1.5, border: '1px solid #eee' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#999', textTransform: 'uppercase', fontWeight: 700 }}>Statement Amount</Typography>
                  <Typography sx={{ fontSize: '1.05rem', fontWeight: 900, color: BRAND }}>
                    {fmt(addLineDialog.statLine.amountIncGst)} inc GST
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, backgroundColor: '#fafafa', borderRadius: 1.5, border: '1px solid #eee' }}>
                  <Typography sx={{ fontSize: '0.65rem', color: '#999', textTransform: 'uppercase', fontWeight: 700 }}>Supplier Ref</Typography>
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#333', fontFamily: 'monospace' }}>
                    {addLineDialog.statLine.supplierRef || addLineDialog.statLine.supplierPoRef || '—'}
                  </Typography>
                </Box>
              </Box>

              {/* Target PO */}
              <FormControl size="small" fullWidth>
                <InputLabel sx={{ fontSize: '0.82rem' }}>Add line to FieldInsight PO</InputLabel>
                <Select value={addLineTargetPo} label="Add line to FieldInsight PO"
                  onChange={e => setAddLineTargetPo(e.target.value)}
                  sx={{ fontSize: '0.82rem' }}>
                  {purchaseOrders.map(po => {
                    const sup = suppliers.find(s => s.id === po.supplierId);
                    return (
                      <MenuItem key={po.id} value={po.id} sx={{ fontSize: '0.82rem' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: '0.82rem', color: FI_ORANGE }}>{po.id}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>{sup?.name ?? ''} · {fmt(po.total)}</Typography>
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              {/* Internal code — optional. Leave blank to resolve on the PO. */}
              <TextField size="small" label="Inventory code (optional)" fullWidth value={addLineCode}
                onChange={e => setAddLineCode(e.target.value.toUpperCase())}
                placeholder="Leave blank to resolve later"
                helperText="Enter an inventory code, or leave blank — the line will surface an 'Enter / Select Code' prompt on the PO"
                inputProps={{ style: { fontSize: '0.82rem', fontFamily: 'monospace' } }} />

              {/* Description preview */}
              <Box sx={{ p: 1.5, backgroundColor: '#f0f7ff', borderRadius: 1.5, border: '1px solid #bbdefb' }}>
                <Typography sx={{ fontSize: '0.65rem', color: '#0277bd', fontWeight: 700, textTransform: 'uppercase', mb: 0.5 }}>Line that will be added</Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ minWidth: 70 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#aaa' }}>Code</Typography>
                    {addLineCode.trim() ? (
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, fontFamily: 'monospace' }}>{addLineCode}</Typography>
                    ) : (
                      <Box component="span" sx={{ display: 'inline-block', backgroundColor: '#c62828', color: '#fff',
                        fontWeight: 800, fontSize: '0.58rem', textTransform: 'uppercase', letterSpacing: '0.3px',
                        px: 0.8, py: 0.3, borderRadius: 0.8, mt: 0.2 }}>
                        Enter / Select Code
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.62rem', color: '#aaa' }}>Description</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: '#333' }}>
                      Supplier Ref: {addLineDialog.statLine.supplierRef || addLineDialog.statLine.supplierPoRef}
                      {addLineDialog.statLine.description ? ` — ${addLineDialog.statLine.description}` : ''}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: '0.62rem', color: '#aaa' }}>Amount</Typography>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: BRAND }}>
                      {fmt(addLineDialog.statLine.amountIncGst / 1.1)} ex
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setAddLineDialog(null)} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
          <Button variant="contained"
            disabled={!addLineTargetPo}
            onClick={() => {
              if (!addLineDialog) return;
              const exGst = addLineDialog.statLine.amountIncGst / 1.1;
              const desc  = `Supplier Ref: ${addLineDialog.statLine.supplierRef || addLineDialog.statLine.supplierPoRef}` +
                            (addLineDialog.statLine.description ? ` — ${addLineDialog.statLine.description}` : '');
              addPOLineDetails([{
                poId:           addLineTargetPo,
                // v1.17 — leave the internal code empty when the user did not
                // pick one, so the new PO line surfaces the red "Enter / Select
                // Code" pill instead of a MISC placeholder.
                inventoryCode:  addLineCode.trim(),
                description:    desc,
                orderedQty:     1,
                receivedToDate: 1,
                unitPrice:      Math.round(exGst * 100) / 100,
                discount:       0,
              }]);
              // Mark this statement line as handled
              setResults(prev => prev.map(r =>
                r.statLine.id === addLineDialog.statLine.id
                  ? { ...r, status: 'matched' as MatchStatus, variance: 0, variancePct: 0, checked: false }
                  : r
              ));
              setAddLineDialog(null);
            }}
            sx={{ backgroundColor: '#FF6A13', textTransform: 'none', fontWeight: 700,
              '&:hover': { backgroundColor: '#F35627' },
              '&.Mui-disabled': { backgroundColor: '#FFE4CC', color: '#C2410C' } }}>
            Add Line to PO
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create PO Dialog ── */}
      <Dialog open={!!createPoDialog} onClose={() => setCreatePoDialog(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', backgroundColor: '#f3e5f5',
              display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DescriptionIcon sx={{ fontSize: 18, color: '#6a1b9a' }} />
            </Box>
            <Box>
              <Typography fontWeight={800}>Create PO — {createPoDialog?.statLine.supplierPoRef}</Typography>
              <Typography variant="caption" color="text.secondary">No matching PO found. Fill in details to create one.</Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {createPoDialog && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box sx={{ p: 1.5, backgroundColor: '#f3e5f5', borderRadius: 1.5 }}>
                <Typography sx={{ fontSize: '0.72rem', color: '#6a1b9a', fontWeight: 700 }}>Statement amount</Typography>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 900, color: '#6a1b9a' }}>
                  {fmt(createPoDialog.statLine.amountIncGst)} inc GST
                </Typography>
              </Box>
              <Autocomplete
                freeSolo
                size="small"
                options={Array.from(new Set(purchaseOrders.map(p => p.jobRef).filter(Boolean)))}
                value={createPoDialog.newJobRef}
                onInputChange={(_e, val) => updateResultField(createPoDialog.statLine.id, 'newJobRef', val)}
                renderInput={params => (
                  <TextField {...params} label="Job reference / Job #" fullWidth
                    placeholder="e.g. JOB-1234 or PLANT-05"
                    inputProps={{ ...params.inputProps, style: { fontSize: '0.82rem' } }} />
                )}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={Array.from(new Set(purchaseOrders.map(p => p.reference).filter(Boolean)))}
                value={createPoDialog.newPostcode}
                onInputChange={(_e, val) => updateResultField(createPoDialog.statLine.id, 'newPostcode', val)}
                renderInput={params => (
                  <TextField {...params} label="Project" fullWidth
                    placeholder="e.g. MEGA-01 or 4560"
                    inputProps={{ ...params.inputProps, style: { fontSize: '0.82rem' } }} />
                )}
              />
              <Alert severity="info" sx={{ fontSize: '0.72rem' }}>
                A draft PO will be created with this reference number. You can edit line items after creation.
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setCreatePoDialog(null)} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
          <Button variant="contained"
            disabled={!createPoDialog?.newJobRef.trim()}
            onClick={() => {
              setResults(prev => prev.map(r =>
                r.statLine.id === createPoDialog?.statLine.id
                  ? { ...r, status: 'matched' as MatchStatus, checked: false }
                  : r
              ));
              setCreatePoDialog(null);
            }}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700,
              '&.Mui-disabled': { backgroundColor: '#e0d5f5', color: BRAND } }}>
            Create Draft PO
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
